/**
 * Signing a create's drafts where the person is (the Request access modal's
 * basket panel): draftSigning.ts with the real browser, server and router.
 *
 * The calls are the signing page's own: the same mint, ceremony, verify and
 * cache refresh. A member with no passkey yet, or none on this device ("Add
 * one here"), is sent to the signing page, which can add one, with router
 * state that brings them back once signed and says the portal sent them (so
 * it shows no code).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getAssertion, webAuthnAvailable } from "modules/members/webauthn";
import {
  refreshAfterSignature,
  useRefetchChallenge,
  useStartRequestSignature,
  verifyChallenge,
} from "state/members/stepUp";

import {
  createDraftSigner,
  handoffNavigation,
  type DraftSigningResult,
  type DraftSigningState,
} from "./draftSigning";

export const useDraftSigning = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const mint = useStartRequestSignature();
  const reread = useRefetchChallenge();
  const [state, setState] = useState<DraftSigningState>({ name: "idle" });

  // The signer is made once; it reaches the latest of these through the ref.
  const latest = useRef({
    mint: mint.mutateAsync,
    reread,
    navigate,
    pathname,
    onDone: null as ((result: DraftSigningResult) => void) | null,
  });
  latest.current.mint = mint.mutateAsync;
  latest.current.reread = reread;
  latest.current.navigate = navigate;
  latest.current.pathname = pathname;

  const [signer] = useState(() =>
    createDraftSigner(
      {
        mint: (requestId) => latest.current.mint(requestId),
        getAssertion,
        verify: verifyChallenge,
        reread: (challengeId) => latest.current.reread(challengeId),
        onSigned: (challengeId) =>
          refreshAfterSignature(queryClient, challengeId),
        canSign: webAuthnAvailable,
      },
      {
        onChange: setState,
        onDone: (result) => {
          const onDone = latest.current.onDone;
          latest.current.onDone = null;
          // The caller closes first: a modal's close can itself navigate
          // (a catalog item's does), and the hand-off has to come after it.
          onDone?.(result);
          const handoff = handoffNavigation(result, latest.current.pathname);
          if (handoff) latest.current.navigate(handoff.to, { state: handoff.state });
        },
      },
    ),
  );

  // A prompt still open when the modal goes lands nowhere.
  useEffect(() => () => signer.dispose(), [signer]);

  return {
    state,
    /** A run is on: the panel is up. */
    active: state.name !== "idle",
    /**
     * Signs these drafts in turn; `onDone` hears how it ended (all signed,
     * stopped, or handed to the signing page). False for none.
     */
    start: (draftIds: string[], onDone: (result: DraftSigningResult) => void) => {
      latest.current.onDone = onDone;
      return signer.start(draftIds);
    },
    sign: signer.sign,
    retry: signer.retry,
    /** "Add one here": the signing page adds a passkey on this device, then signs. */
    addHere: signer.addHere,
    stop: signer.stop,
  };
};

export type DraftSigning = ReturnType<typeof useDraftSigning>;
